import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FlexStack, InputWrapper, StyledLabel } from '../../../styles/EditDeleteStyles';
import InputText from '../../../components/InputText';
import { TextArea } from '../../../components/InputTextArea';
import { EmailRecipientsSelect } from './Shared/EmailRecipientsSelect';
import { ActionBarStyled, FlexboxStyled } from '../../../components/PageAction/styles';
import { LabelStyled } from '../../../components/InputText/styles';
import { SelectStyled } from '../../../components/InputSelect/styles';
import { colours } from '../../../styles/theme';
import { ButtonStyled } from '../../../components/Button/styles';
import { BannerImageSelect } from './Shared/BannerImageSelect';
import { RecipientsState } from './Shared/types';
import { Sending } from './Shared/Sending';

type ListingContent = {
  format: 'bulleted' | 'numbered';
  title: string;
  listingItems: string[];
};

type PlatformwideNotificationProps = {
  onChange: (data: any) => void;
  error?: any;
  loading?: boolean;
};

const ListingContentForm = ({
  index,
  listingContent: { format, title, listingItems },
  handleChange,
  handleDelete,
}: {
  index: number;
  listingContent: ListingContent;
  handleChange: (params: { index: number; changedContent: Partial<ListingContent> }) => void;
  handleDelete: (params: { index: number }) => void;
}) => {
  const listingFormatFormKey = `listing_format_${index}`;
  const listingTitleFormKey = `listing_title_${index}`;

  return (
    <>
      <StyledLabel htmlFor={listingFormatFormKey}>Listing format</StyledLabel>
      <SelectStyled
        onChange={({ currentTarget }: React.ChangeEvent<HTMLSelectElement>) => {
          handleChange({
            index,
            changedContent: { format: currentTarget.value as ListingContent['format'] },
          });
        }}
        value={format}
      >
        {[
          {
            value: 'bulleted',
            label: 'Bulleted listing',
          },
          {
            value: 'numbered',
            label: 'Numbered listing',
          },
        ].map(({ label, value }, i) => (
          <option value={value} key={i}>
            {label}
          </option>
        ))}
      </SelectStyled>
      <StyledLabel htmlFor={listingFormatFormKey}>Listing title</StyledLabel>
      <InputText
        id={listingTitleFormKey}
        keyName={listingTitleFormKey}
        placeholder="Please share a title"
        inputValue={title}
        handleHandle={(input) => {
          handleChange({ index, changedContent: { title: input } });
        }}
        padding={false}
        theme="form"
      />
      <StyledLabel htmlFor={listingFormatFormKey}>Listing items</StyledLabel>
      {listingItems.map((itemContent, listingItemIndex) => (
        <div style={{ marginBottom: '0.5em' }} key={listingItemIndex}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <InputText
              id={`listing_item_${listingItemIndex}`}
              keyName={`listing_item_${listingItemIndex}`}
              placeholder="Add your listing item"
              inputValue={itemContent}
              handleHandle={(input) => {
                const updatedItems = listingItems.map((item, i) =>
                  i === listingItemIndex ? input : item
                );
                handleChange({ index, changedContent: { listingItems: updatedItems } });
              }}
              padding={false}
              theme="form"
            />
            <FontAwesomeIcon
              icon={faTrash}
              color={colours.colour18}
              style={{
                marginLeft: '1em',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                userSelect: 'none',
              }}
              role="button"
              onClick={() => {
                const updatedItems = listingItems.filter((_e, i) => i !== listingItemIndex);
                handleChange({ index, changedContent: { listingItems: updatedItems } });
              }}
            />
          </div>
        </div>
      ))}
      <div
        style={{
          marginTop: '1em',
          marginBottom: '1em',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
        }}
        tabIndex={0}
        role="button"
        aria-label="Add listing item"
        onClick={() =>
          handleChange({ index, changedContent: { listingItems: [...listingItems, ''] } })
        }
        onKeyDown={() => 1}
      >
        <FontAwesomeIcon icon="plus" color={colours.colour11} />
        <span style={{ marginLeft: '1em' }}>Add listing item</span>
      </div>
      {index > 0 ? (
        <ButtonStyled theme="warning" onClick={() => handleDelete({ index })}>
          Remove this listing content
        </ButtonStyled>
      ) : null}
      <hr style={{ borderTop: `10px ${colours.colour17}`, color: 'red', width: '100%' }} />
    </>
  );
};

export function PlatformwideNotification({
  onChange,
  error,
  loading,
}: PlatformwideNotificationProps): JSX.Element {
  const [recipients, setRecipients] = useState<RecipientsState>({ recipients: 'all' });
  const [subject, setSubject] = useState<string>('');
  const subjectFormKey = 'platform_notification_subject';
  const [openingMessage, setOpeningMessage] = useState<string>('');
  const openingMessageFormKey = 'platform_notification_opening_message';
  const [listingContents, setListingContents] = useState<ListingContent[]>([
    { format: 'bulleted', title: '', listingItems: [''] },
  ]);
  const [bannerImage, setBannerImage] = useState<File>({} as File);
  const [bannerImageName, setBannerImageName] = useState<string>('');
  const [ctaButtonText, setCtaButtonText] = useState<string>('');
  const ctaButtonTextFormKey = 'cta_button_text';
  const [ctaText, setCtaText] = useState<string>('');
  const ctaTextFormKey = 'cta_text';

  const handleListingContentChange = ({
    index,
    changedContent,
  }: {
    index: number;
    changedContent: Partial<ListingContent>;
  }) => {
    setListingContents((prev) =>
      prev.map((content, i) => (i === index ? { ...content, ...changedContent } : content))
    );
  };
  const handleDeleteListingContent = ({ index }: { index: number }) => {
    setListingContents((prev) => prev.filter((_content, i) => i !== index));
  };

  React.useEffect(() => {
    setBannerImageName(bannerImage?.name || '');
  }, [bannerImage]);

  React.useEffect(() => {
    onChange({
      recipients,
      subject,
      openingMessage,
      listingContents,
      ctaButtonText,
      ctaText,
      bannerImage,
      bannerImageName,
    });
  }, [
    recipients,
    subject,
    openingMessage,
    listingContents,
    ctaButtonText,
    ctaText,
    bannerImage,
    bannerImageName,
    onChange,
  ]);

  if (loading) return <Sending />;
  return (
    <>
      <InputWrapper>
        <EmailRecipientsSelect
          initialValue={recipients}
          handleChange={(update) => {
            const updated = { ...recipients, ...update };
            setRecipients(updated);
          }}
        />
      </InputWrapper>
      <InputWrapper>
        <InputText
          id={subjectFormKey}
          keyName={subjectFormKey}
          label="Subject"
          error={error?.subject}
          placeholder="Email subject for platform-wide notification"
          inputValue={subject}
          handleHandle={setSubject}
          padding={false}
          theme="form"
        />
      </InputWrapper>
      <BannerImageSelect
        handleChange={(value: File) => setBannerImage(value)}
        fileName={bannerImage?.name}
        error={error?.bannerImageName}
      />
      <InputWrapper>
        <TextArea
          id={openingMessageFormKey}
          keyName={openingMessageFormKey}
          label="Open Text"
          error={error?.openingMessage}
          placeholder="Add your opening message here"
          textValue={openingMessage}
          handleHandle={setOpeningMessage}
        />
      </InputWrapper>
      <InputWrapper>
        <ActionBarStyled>
          <FlexboxStyled style={{ marginBottom: 0 }}>
            <LabelStyled style={{ color: 'black' }}>
              Listing Content
              <br />
              Optional
            </LabelStyled>
            <FlexStack>
              {listingContents.map((listingContent, index) => (
                <ListingContentForm
                  key={index}
                  index={index}
                  listingContent={listingContent}
                  handleChange={handleListingContentChange}
                  handleDelete={handleDeleteListingContent}
                />
              ))}
              <br />
              <ButtonStyled
                onClick={() =>
                  setListingContents([
                    ...listingContents,
                    { format: 'bulleted', title: '', listingItems: [''] },
                  ])
                }
              >
                Add more listing content
              </ButtonStyled>
            </FlexStack>
          </FlexboxStyled>
        </ActionBarStyled>
      </InputWrapper>
      <InputWrapper>
        <TextArea
          id={ctaTextFormKey}
          keyName={ctaTextFormKey}
          label="Call to Action Text"
          error={error?.ctaText}
          placeholder="Add your call to action message here"
          textValue={ctaText}
          handleHandle={setCtaText}
        />
      </InputWrapper>
      <InputWrapper>
        <InputText
          id={ctaButtonTextFormKey}
          keyName={ctaButtonTextFormKey}
          label={
            <div style={{ lineHeight: 1.4 }}>
              <div>Call to Action</div>
              <div>Button Text</div>
            </div>
          }
          error={error?.ctaButtonText}
          placeholder="Add call to action"
          inputValue={ctaButtonText ?? ''}
          handleHandle={setCtaButtonText}
          padding={false}
          theme="form"
        />
      </InputWrapper>
    </>
  );
}
